import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { isEmpty, omitBy } from 'lodash'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { openWorkspaceSetupStep } from 'app/components/integrations/workspaceSetupWizard'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const createSchema = z
  .object({
    locationId: z.string().min(1, { message: `A Location is required` }),
    pages: z
      .array(
        z.object({
          id: z.string(),
          meta: z.object({ type: z.string(), formIds: z.array(z.string()).optional() }),
        }),
      )
      .transform((pages) => {
        // Filter out deselect options
        const validPages = pages.filter((page) => page.id !== `0`)
        return validPages
      }),
    forms: z
      .array(
        z.object({
          id: z.string(),
          value: z.string(),
          meta: z.object({ type: z.string() }).optional(),
        }),
      )
      .optional()
      .transform((forms) => {
        // Handle forms transformation
        if (!forms || forms.length === 0) {
          return undefined // No forms selected means "All Forms"
        }

        // Check if "All Forms" deselect option is selected
        const hasAllForms = forms.some((f) => f.id === `0` || f.meta?.type === `deselectAll`)
        if (hasAllForms) {
          return undefined // "All Forms" selected
        }

        // Return specific form IDs, filtering out empty values
        return forms.map((f) => f.value).filter((v) => v !== ``)
      }),
  })
  .transform((data) => {
    // Final transformation to combine pages and forms
    const finalPages = data.pages.map((page) => ({
      id: page.id,
      formIds: data.forms, // forms is already processed and will be undefined for "All Forms"
    }))

    return {
      locationId: data.locationId,
      pages: finalPages,
      forms: data.forms, // Keep forms for debugging if needed
    }
  })

export const createFacebookLeadsValidator = withZod(
  createSchema.refine(
    (data) => {
      // Validate that at least one page is selected
      return data.pages && data.pages.length > 0
    },
    {
      message: `At least one page must be selected`,
      path: [`pages`],
    },
  ),
)

export function FacebookLeadsFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: { locationId: string; locationName: string; facebookLeadsPages: LocationType[`facebookLeadsPages`] }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [pagesInFlight, setPagesInFlight] = useState(false)
  const [locationOptions, setLocationOptions] = useState<OptionType[]>([])
  const [pageOptions, setPageOptions] = useState<OptionType[]>([])
  const [formOptions, setFormOptions] = useState<OptionType[]>()
  const [selectedPageOptions, setSelectedPageOptions] = useState<OptionType[]>([])
  const [selectedFormOptions, setSelectedFormOptions] = useState<OptionType[]>([])
  const [confirm] = useConfirmation()
  const [isSaving, setIsSaving] = useState(false)
  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [workspaceConfigured, setWorkspaceConfigured] = useState<boolean | undefined>(undefined)

  const fetchPageOptions = (refresh?: boolean) => {
    debug(`facebook-leads`, `fetching page options`, refresh)

    const composedSearchParams = new URLSearchParams(
      omitBy(
        {
          refresh: refresh ? `true` : ``,
        },
        isEmpty,
      ),
    )
    const url = `/api/integrations/facebook-leads/pages?${composedSearchParams.toString()}`

    setLocationOptions([])
    setPageOptions([])
    if (url) {
      bg(async () => {
        setPagesInFlight(true)
        let response: any
        try {
          const res = await window.fetch(url, { credentials: `include` })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          response = await res.json()
        } catch (err) {
          logError(`Failed to load Facebook Leads pages:`, err)
          setPagesInFlight(false)
          if (currentValues?.locationId) {
            setLoadError(`Unable to load Facebook pages. Please try again later.`)
          } else {
            goBackToSelectIntegrationModal()
          }
          return
        }
        setPagesInFlight(false)
        debug(`facebook-leads`, `pages response received`, response)
        if (response?.error) {
          debug(`facebook-leads`, `Error in pages response`, response.error)
          if (currentValues?.locationId) {
            setLoadError(response.error)
          } else {
            goBackToSelectIntegrationModal()
          }
        } else {
          debug(`facebook-leads`, `Processing pages response`, response)

          // Process locations
          if (response?.locations) {
            debug(`facebook-leads`, `Found locations in response`, response.locations)
            const locationOpts = response.locations.map((location: any) => ({
              id: location.id,
              name: location.name,
              value: location.value,
              meta: location.meta,
            }))
            debug(`facebook-leads`, `Mapped location options`, locationOpts)
            setLocationOptions(locationOpts)
            debug(`facebook-leads`, `setLocationOptions called successfully`)

            // Auto-select the first location if not editing existing
            if (!currentValues?.locationId && locationOpts.length > 0) {
              debug(`facebook-leads`, `Auto-selecting first location:`, locationOpts[0])
              setSelectedLocation(locationOpts[0])
            }
          } else {
            debug(`facebook-leads`, `No locations found in response`)
          }

          // Process pages - separated into try-catch to prevent errors from stopping execution
          try {
            debug(`facebook-leads`, `Checking for pages in response:`, response?.pages)
            if (response?.pages) {
              debug(`facebook-leads`, `Processing pages:`, response.pages)
              const responseOptions = response.pages.map((page: any) => ({
                id: page.value || page.name,
                name: page.name,
                value: page.value,
                meta: {
                  type: `page`,
                  formIds: [] as string[],
                },
              }))
              debug(`facebook-leads`, `Mapped page options:`, responseOptions)
              setPageOptions(responseOptions)
              setIsFirstPageLoad(false) // Mark first load as complete
              debug(`facebook-leads`, `setPageOptions called with`, responseOptions.length, `pages`)
              if (currentValues?.facebookLeadsPages?.length) {
                const currentPages = currentValues.facebookLeadsPages
                  .map((page) => {
                    const option = responseOptions.find((opt: OptionType) => opt.value === page.id)
                    if (option) {
                      option.meta.formIds = page.formIds || []
                    }
                    return option
                  })
                  .filter(Boolean) as OptionType[]
                setSelectedPageOptions(currentPages)
              }
            } else {
              debug(`facebook-leads`, `No pages found in response`)
            }
          } catch (error) {
            logError(`Error processing Facebook pages in modal:`, error)
          }
        }
      })
    }
  }

  const fetchFormOptions = () => {
    const locationId = currentValues?.locationId || selectedLocation?.value
    if (!locationId || selectedPageOptions.length === 0) return

    const pageIds = selectedPageOptions.map((p) => p.value).join(`,`)
    const url = `/api/integrations/facebook-leads/${locationId}/forms?pageIds=${pageIds}`

    setFormOptions(undefined)
    if (url) {
      bg(async () => {
        const response = await window.fetch(url, { credentials: `include` }).then((r) => r.json())
        debug(`facebook-leads`, `forms response received`, response)

        if (response && response.forms) {
          const responseOptions = response.forms.map((form: any) => ({
            id: form.value || form.name,
            name: form.name,
            value: form.value,
            meta: {
              type: `form`,
              pageId: form.meta?.pageId,
            },
          }))
          debug(`facebook-leads`, `Mapped form options:`, responseOptions)
          setFormOptions(responseOptions)

          // Pre-select existing forms if editing
          if (currentValues?.facebookLeadsPages?.length) {
            // Get all formIds from all pages (they should be the same across pages in current schema)
            const existingFormIds = currentValues.facebookLeadsPages.flatMap((page) => page.formIds || [])
            debug(`facebook-leads`, `Existing form IDs from location:`, existingFormIds)

            if (existingFormIds.length > 0) {
              // Find matching form options for existing form IDs
              const existingFormOptions = responseOptions.filter((option) => existingFormIds.includes(option.value))
              debug(`facebook-leads`, `Matched existing form options:`, existingFormOptions)
              setSelectedFormOptions(existingFormOptions)
            } else {
              // formIds is undefined/empty means "All Forms" was selected
              debug(`facebook-leads`, `No specific form IDs, defaulting to All Forms`)
              setSelectedFormOptions([])
            }
          } else if (responseOptions.length === 0) {
            // If no specific forms available, auto-select "All Forms"
            // This handles both empty responses and Page Access Token errors
            debug(`facebook-leads`, `No forms available, showing All Forms fallback`)
            setSelectedFormOptions([{ name: `All Forms`, id: `all`, value: `all`, meta: { type: `allForms` } }])
          }
        } else if (response && response.error) {
          // Handle API errors - redirect back to integrations list
          debug(`facebook-leads`, `Forms API error, redirecting back`, response.error)
          goBackToSelectIntegrationModal()
        }
      })
    }
  }

  const checkWorkspaceIntegration = () => {
    // Skip workspace validation if we're editing an existing integration
    if (currentValues?.locationId) {
      setWorkspaceConfigured(true)
      return
    }

    bg(async () => {
      try {
        const res = await window.fetch(`/api/integrations/facebook-leads/workspace`, { credentials: `include` })
        // The endpoint signals a missing workspace integration with a 400 + error body.
        // Any client error is treated as "not configured" so the hand-off stays
        // resilient to backend contract drift; 5xx/network stay verification failures.
        if (!res.ok && res.status < 500) {
          setWorkspaceConfigured(false)
          return
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const response = await res.json()
        debug(`facebook-leads`, `workspace response`, response)
        if (response?.error) {
          setWorkspaceConfigured(false)
          setLoadError(response.error)
        } else {
          setWorkspaceConfigured(true)
        }
      } catch (err) {
        logError(`Failed to check Facebook Leads workspace integration:`, err)
        setLoadError(`Unable to verify the Facebook Leads workspace connection. Please try again later.`)
      }
    })
  }

  const onCreateCompleted = (data: any) => {
    setIsSaving(false)
    if (data.success) {
      notifySuccess(`Facebook Leads integration saved successfully`)
      closeModal()
      onAdd()
    } else {
      notifyError(`Failed to save Facebook Leads integration: ${data.error}`)
    }
  }

  const onSubmit = () => {
    setIsSaving(true)
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const goToWorkspaceSetup = () => {
    closeModal()
    openWorkspaceSetupStep(`Facebook Leads`, onAdd)
  }

  const onRefreshClick = () => {
    fetchPageOptions(true)
  }

  const onRemove = () => {
    return confirm({
      title: `Remove Facebook Leads Integration`,
      description: `Removing this integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      window
        .fetch(`/api/integrations/facebook-leads`, {
          method: `DELETE`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({ locationId: currentValues?.locationId! }),
        })
        .then(() => {
          notifySuccess(`Facebook Leads integration removed`)
          closeModal()
          onAdd()
        }),
    )
  }

  useEffect(() => {
    // Check workspace integration first
    checkWorkspaceIntegration()
  }, [])

  // Fetch page options after workspace is confirmed
  useEffect(() => {
    if (workspaceConfigured) {
      fetchPageOptions()
    }
  }, [workspaceConfigured])

  useEffect(() => {
    if (selectedPageOptions.length > 0) {
      fetchFormOptions()
    } else {
      // Clear form options when no pages are selected
      setFormOptions(undefined)
      setSelectedFormOptions([])
    }
  }, [selectedPageOptions])

  // Show loading state while checking workspace configuration
  if (workspaceConfigured === undefined && !loadError) {
    return (
      <>
        <div className="w-[600px] px-6 py-4">
          <div className="flex items-center justify-center py-8">
            <Spinner size={5} />
          </div>
        </div>
        <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
          <div className="flex items-center justify-between">
            <div></div>
            <div className="flex gap-2">
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            </div>
          </div>
        </div>
      </>
    )
  }

  // Workspace connection missing: hand off to the workspace setup step.
  if (workspaceConfigured === false) {
    return (
      <>
        <div className="w-[600px] space-y-4 px-6 py-4">
          <p className="text-sm text-default">
            Facebook Leads needs a workspace-level connection before locations can use it. Set it up now and you&apos;ll
            come right back to this step.
          </p>
        </div>
        <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
          <div className="flex items-center justify-between">
            <div>
              {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
            </div>
            <div className="flex gap-2">
              {currentValues ? (
                <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
              ) : (
                <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
              )}
              <Button
                label="Set up workspace connection"
                mode="primary"
                size="md"
                onClick={() => goToWorkspaceSetup()}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  // Facebook API failure with a configured workspace: keep the existing error rendering.
  if (loadError) {
    return (
      <>
        <div className="w-[600px] space-y-4 px-6 py-4">
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <code className="text-xs text-red-700">{loadError}</code>
          </div>
          <p className="text-sm text-default">
            Unable to load Facebook pages. You may need to re-connect Facebook Leads at the workspace level. You can try
            again later{currentValues ? ` or remove this integration` : ``}.
          </p>
        </div>
        <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
          <div className="flex items-center justify-between">
            <div>
              {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
            </div>
            <div className="flex gap-2">
              {currentValues ? (
                <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
              ) : (
                <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
              )}
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createFacebookLeadsValidator}
        reloadDocument={false}
        shouldPreventDefault={true}
        onPreventDefault={(data) => onCreateCompleted(data)}
        onSubmit={onSubmit}
        action="/api/integrations/facebook-leads"
        method={currentValues ? `PUT` : `POST`}
        hideButtons={true}
        hiddenInputs={[]}
      >
        {workspaceConfigured && pageOptions.length === 0 ? (
          <div className="col-span-6 flex items-center justify-center py-8">
            <div className="flex items-center gap-2">
              <Spinner size={5} />
              {isFirstPageLoad && (
                <span className="text-body-sm text-subtle">Please be patient while we load Facebook pages...</span>
              )}
            </div>
          </div>
        ) : (
          <>
            {currentValues ? (
              <>
                <FormSelect
                  name="ignored-because-it-is-disabled"
                  cols={5}
                  label="Trackable Location"
                  options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
                  value={currentValues.locationId}
                  disabled
                  labelClassName="text-body-sm text-strong"
                />
                <FormHiddenInput name="locationId" value={currentValues.locationId} />
              </>
            ) : (
              <>
                {locationOptions.length > 0 ? (
                  <FormSelect
                    cols={5}
                    name="locationId"
                    label="Trackable Location"
                    options={locationOptions.map((loc) => ({
                      label: loc.name + (loc.meta?.configured ? ` (Already configured)` : ``),
                      value: loc.value,
                      disabled: loc.meta?.configured,
                    }))}
                    value={selectedLocation?.value}
                    onChange={(e) => {
                      const selectedOpt = locationOptions.find((opt) => opt.value === e.target.value)
                      setSelectedLocation(selectedOpt)
                    }}
                    labelClassName="text-body-sm text-strong"
                    className="text-body-sm"
                    required
                  />
                ) : null}
              </>
            )}
            {(currentValues?.locationId || selectedLocation) && !!pageOptions.length ? (
              <>
                <FormComboMultiSelect
                  cols={4}
                  name="pages"
                  label="Facebook Pages"
                  values={selectedPageOptions}
                  options={pageOptions}
                  labelClassName="text-body-sm text-strong"
                  className="text-body-sm"
                  onChange={(val) => {
                    debug(`facebook-leads`, `Page selection onChange called with:`, val)
                    setSelectedPageOptions(val)
                    debug(`facebook-leads`, `selectedPageOptions updated to:`, val)
                  }}
                  newLineError={false}
                  checkboxSide="left"
                  showErrorIcon
                />
                <div className="col-span-1 flex items-center justify-center pt-5">
                  <ArrowPathIcon
                    className={classNames(`ml-2 h-5 w-5 cursor-pointer`, pagesInFlight && `animate-spin`)}
                    onClick={() => onRefreshClick()}
                  />
                </div>
              </>
            ) : null}
          </>
        )}
        {selectedPageOptions.length === 0 ? null : formOptions != null ? (
          <FormComboMultiSelect
            cols={5}
            name="forms"
            label="Forms"
            values={selectedFormOptions}
            deselectAllOptions={[{ name: `All Forms`, id: `0`, value: ``, meta: { type: `deselectAll` } }]}
            enableDeselectAllOption={true}
            options={formOptions.length > 0 ? formOptions : []}
            labelClassName="text-body-sm text-strong"
            className="text-body-sm"
            onChange={(val) => {
              setSelectedFormOptions(val)
            }}
            placeholder="All Forms"
            newLineError={false}
            checkboxSide="left"
            showErrorIcon
          />
        ) : (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}

            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !pageOptions.length}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </>
  )
}
